import React, { useState } from 'react';
import './DealReturns.scss';
import { NSSwitch, NSButton } from 'bricks';
import useLocalStorage from 'helpers/localStorage/useLocalStorage';
import { PageSpecificKeys } from 'helpers/localStorage/constants';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { Deal } from 'views/Deals/types';
import RenderIf from 'components/RenderIf/RenderIf';
import { useGetProFormasByDeal } from 'queries/ProForma/useGetProFormasByDeal';
import { useGetProFormaStats } from '../hooks/useGetProFormaStats';
import ReturnsCard from './ReturnsCard/ReturnsCard';
import { TARGET_RETURNS_CONFIG, KEY_STATS_CONFIG } from './constants';

export interface TargetReturnsValues {
    cost: number | null;
    equity: number | null;
    profit: number | null;
}

export interface KeyStatsValues {
    leveredIrr: number | null;
    leveredEmx: number | null;
    developmentSpread: number | null;
    returnOnCost: number | null;
    returnOnEquity: number | null;
    profitReturn: number | null;
}

interface DealReturnsProps {
    deal: Deal;
    onSave: (values: TargetReturnsValues) => void;
}

const DealReturns: React.FC<DealReturnsProps> = ({ deal, onSave }) => {
    const [values, setValues] = useState<TargetReturnsValues>({
        cost: deal.targetReturnOnCost ?? null,
        equity: deal.targetReturnOnEquity ?? null,
        profit: deal.targetProfitReturn ?? null,
    });

    const [editingValues, setEditingValues] = useState<TargetReturnsValues>({
        cost: deal.targetReturnOnCost || null,
        equity: deal.targetReturnOnEquity || null,
        profit: deal.targetProfitReturn || null,
    });

    const handleInputChange = (field: keyof TargetReturnsValues, value: number) => {
        setEditingValues(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        setValues(editingValues);
        setIsEditing(false);
        onSave(editingValues);
    };

    const handleCancel = () => {
        setEditingValues(values);
        setIsEditing(false);
    };

    const [isEditing, setIsEditing] = useState(false);
    const [showTargetReturns, setShowTargetReturns] = useLocalStorage<boolean>(PageSpecificKeys.ShowTargetReturns, false);

    const { data: proformas, isLoading: isLoadingProFormas } = useGetProFormasByDeal({
        dealId: deal.id,
        proFormaStatusIds: [PRO_FORMA_STATUS.SUBSTANTIALLY_COMPLETE],
    });

    const activeProforma = proformas?.find(proforma => proforma.isActive);
    const hasSubstantiallyCompleteProForma = !!activeProforma;

    const { keyStats, isLoading: isLoadingProFormaRoom, activeProFormaName } = useGetProFormaStats({
        dealId: deal.id,
        proFormaId: activeProforma?.id,
    });

    const handleToggleShowTargetReturns = () => {
        setShowTargetReturns(!showTargetReturns);
    };

    const handleEditTargetReturns = () => {
        if (!showTargetReturns) setShowTargetReturns(true);
        setIsEditing(true);
    };

    const headerTitle = hasSubstantiallyCompleteProForma && !showTargetReturns
        ? activeProFormaName
        : 'Target returns';

    const showEditButton = !isEditing && !isLoadingProFormas;
    const showSwitch = !isEditing && hasSubstantiallyCompleteProForma && !isLoadingProFormas;

    return (
        <div className="container-fluid DealReturns">
            <div className="d-flex justify-content-between align-items-end">
                <h3 className="text-white font-16 font-weight-semibold">{headerTitle}</h3>
                <div className="d-flex flex-column align-items-end">
                    <RenderIf
                        isTrue={showEditButton}
                    >
                        <NSButton
                            className="text-primary px-0 heap-deal-dashboard-overview-edit-target-returns"
                            callback={handleEditTargetReturns}
                        >
                            Edit target returns
                        </NSButton>
                    </RenderIf>

                    <RenderIf
                        isTrue={showSwitch}
                    >
                        <NSSwitch
                            id="show-target-returns"
                            name="showTargetReturns"
                            checked={showTargetReturns}
                            onChange={handleToggleShowTargetReturns}
                            label="Show target returns"
                            containerClassName="mb-1"
                        />
                    </RenderIf>
                </div>
            </div>

            <RenderIf
                isTrue={hasSubstantiallyCompleteProForma && !showTargetReturns}
                isLoading={hasSubstantiallyCompleteProForma && !showTargetReturns && isLoadingProFormaRoom}
            >
                <div className="row gx-3">
                    {KEY_STATS_CONFIG.slice(0, 3).map(config => (
                        <div key={config.field} className="col-md-4 mb-3">
                            <ReturnsCard
                                isEditing={false}
                                title={config.title}
                                value={keyStats[config.field as keyof KeyStatsValues]}
                                unit={config.unit}
                            />
                        </div>
                    ))}
                </div>
                <div className="row gx-3">
                    {KEY_STATS_CONFIG.slice(3).map(config => (
                        <div key={config.field} className="col-md-4">
                            <ReturnsCard
                                isEditing={false}
                                title={config.title}
                                value={keyStats[config.field as keyof KeyStatsValues]}
                                unit={config.unit}
                            />
                        </div>
                    ))}
                </div>
            </RenderIf>

            <RenderIf
                isTrue={!hasSubstantiallyCompleteProForma || showTargetReturns}
                isLoading={isLoadingProFormas}
            >
                <div className="row gx-3">
                    {TARGET_RETURNS_CONFIG.map(config => (
                        <div key={config.field} className="col">
                            <ReturnsCard
                                isEditing={isEditing}
                                title={config.title}
                                field={config.field as keyof TargetReturnsValues}
                                value={
                                    isEditing
                                        ? editingValues[config.field as keyof TargetReturnsValues]
                                        : values[config.field as keyof TargetReturnsValues]
                                }
                                min={config.min}
                                max={config.max}
                                unit={config.unit}
                                hasRange={config.hasRange}
                                handleInputChange={handleInputChange}
                            />
                        </div>
                    ))}
                </div>
            </RenderIf>

            <RenderIf isTrue={isEditing}>
                <div className="d-flex justify-content-end mt-3">
                    <NSButton text="Cancel" callback={handleCancel} className="bg-none text-dark" />
                    <NSButton
                        text="Save target returns"
                        className="btn btn-primary ms-2 heap-deal-dashboard-overview-save-target-returns"
                        callback={handleSave}
                    />
                </div>
            </RenderIf>
        </div>
    );
};

export default DealReturns;
